/**
 * Admin Frame — full-screen iframe modal that loads `/admin/index.html`
 * with a bypass URL param so admins don't need to re-enter the GAS-backed
 * password.
 *
 * The bypass is a server-validated trust chain:
 *   1. Banner only renders if `isAdminPhone(LocalState.phone)` is true.
 *   2. The iframe URL carries `?fromApp=1` plus the current Firebase uid.
 *   3. The admin page (modified) checks: uid is in `/config/admins/uids`
 *      AND the `fromApp` flag is set → skips the login screen.
 *
 * Because the iframe is same-origin, the parent's Firebase Auth session
 * is automatically shared — no extra sign-in required.
 */

import { auth } from '../../core/firebase.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('admin-frame');

const OVERLAY_ID = 'adminFrameOverlay';

/** Open the admin frame modal. Idempotent. */
export function open() {
  const overlay = ensureModal();
  document.body.appendChild(overlay);
  overlay.classList.add('open');
  // Reload the iframe each time so stats refresh and any old state clears.
  const iframe = /** @type {HTMLIFrameElement | null} */ (overlay.querySelector('iframe'));
  if (iframe) iframe.src = buildAdminUrl();
  document.body.classList.add('admin-frame-open');
  log.info('opened');
}

export function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  overlay.classList.remove('open');
  document.body.classList.remove('admin-frame-open');
  // Clear src so the iframe doesn't keep running in the background.
  const iframe = /** @type {HTMLIFrameElement | null} */ (overlay.querySelector('iframe'));
  if (iframe) iframe.src = 'about:blank';
}

function buildAdminUrl() {
  const uid = auth().currentUser?.uid || '';
  const params = new URLSearchParams({ fromApp: '1', uid });
  return `/admin/index.html?${params.toString()}`;
}

function ensureModal() {
  let ov = document.getElementById(OVERLAY_ID);
  if (ov) return ov;
  ov = document.createElement('div');
  ov.id = OVERLAY_ID;
  ov.className = 'admin-frame-overlay';
  ov.innerHTML = `
    <header class="admin-frame-header">
      <div class="admin-frame-title">⚙️ פאנל אדמין</div>
      <button type="button" class="admin-frame-close" aria-label="סגור">✕</button>
    </header>
    <iframe class="admin-frame-iframe" src="about:blank" allow="clipboard-read; clipboard-write"></iframe>
  `;
  ov.querySelector('.admin-frame-close')?.addEventListener('click', close);
  // ESC key closes the modal (desktop usability).
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && ov?.classList.contains('open')) close();
  });
  return ov;
}
